/**
 * Backend → frontend shape adapters.
 *
 * The Worker historically returned snake_case rows straight from D1. After the
 * serializer migration (apps/api/src/lib/serializers.ts) the API matches these
 * `*Camel` types exactly, but the adapter remains here as a defense in depth:
 *   - Older deploys that haven't picked up the serializer yet still work.
 *   - Anything that hits the SPA HTML fallback (`https://chat.dwsm.qzz.io/api/...`)
 *     and gets HTML parsed as JSON stops at the first error rather than
 *     crashing the entire chat view.
 *
 * Every adapter is total: given `undefined` or a partial object, it returns
 * a value matching the frontend type with safe defaults.
 */

import type {
  Conversation,
  ConversationMember,
  Message,
  MessageReadBy,
  Poll,
  PollOption,
  Reaction,
  User,
  UserProfile,
} from "./api";

/** Returns true iff `v` is a non-null, non-undefined object. */
function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Coerce a Unix-ms number into an ISO string; pass through anything else. */
function tsToIso(v: unknown): string {
  if (typeof v === "string" && v) return v;
  if (typeof v === "number" && Number.isFinite(v)) return new Date(v).toISOString();
  // Last resort: epoch. Lets the UI render something rather than NaN.
  return new Date(0).toISOString();
}

function tsToIsoOrNull(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === "string" && v) return v;
  if (typeof v === "number" && Number.isFinite(v)) return new Date(v).toISOString();
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Field-level snake → camel coercion                                          */
/* -------------------------------------------------------------------------- */

export function coerceReaction(v: unknown): Reaction {
  const r = isObject(v) ? v : {};
  return {
    emoji: typeof r.emoji === "string" ? r.emoji : "",
    count: typeof r.count === "number" ? r.count : 0,
    myReacted:
      typeof r.myReacted === "boolean"
        ? r.myReacted
        : typeof r.my_reacted === "boolean"
          ? r.my_reacted
          : false,
  };
}

export function coerceReactions(arr: unknown): Reaction[] {
  if (!Array.isArray(arr)) return [];
  return arr.map((v) => coerceReaction(v));
}

/**
 * User profile (F-008). Identical to `User` plus `createdAt` — the public
 * profile endpoint exposes when the account was created.
 */
export function coerceUserProfile(v: unknown): UserProfile {
  const base = coerceUser(v);
  const r = isObject(v) ? v : {};
  return {
    ...base,
    createdAt: tsToIso(r.createdAt ?? r.created_at),
  };
}

export function coerceUser(v: unknown): User {
  const r = isObject(v) ? v : {};
  return {
    id: typeof r.id === "string" ? r.id : "",
    username: typeof r.username === "string" ? r.username : "",
    displayName:
      typeof r.displayName === "string"
        ? r.displayName
        : typeof r.display_name === "string"
          ? r.display_name
          : "",
    avatarUrl:
      typeof r.avatarUrl === "string"
        ? r.avatarUrl
        : typeof r.avatar_url === "string"
          ? r.avatar_url
          : null,
    lastSeen: tsToIsoOrNull(
      "lastSeen" in r ? r.lastSeen : r.last_seen,
    ),
  };
}

export function coerceMessage(v: unknown): Message {
  const r = isObject(v) ? v : {};
  const sender = "sender" in r ? r.sender : undefined;
  const replyTo = "replyTo" in r ? r.replyTo : r.reply_to;
  const poll = "poll" in r ? r.poll : undefined;
  return {
    id: typeof r.id === "string" ? r.id : "",
    conversationId:
      typeof r.conversationId === "string"
        ? r.conversationId
        : typeof r.conversation_id === "string"
          ? r.conversation_id
          : "",
    senderId:
      typeof r.senderId === "string"
        ? r.senderId
        : typeof r.sender_id === "string"
          ? r.sender_id
          : "",
    sender: sender ? coerceUser(sender) : null,
    content: typeof r.content === "string" ? r.content : "",
    type: r.type === "poll" || r.type === "system" ? r.type : "text",
    replyToId:
      typeof r.replyToId === "string"
        ? r.replyToId
        : typeof r.reply_to_id === "string"
          ? r.reply_to_id
          : null,
    replyTo: replyTo ? coerceMessage(replyTo) : null,
    poll: poll ? coercePoll(poll) : null,
    createdAt: tsToIso(r.createdAt ?? r.created_at),
    editedAt: tsToIsoOrNull(r.editedAt ?? r.edited_at),
    deletedAt: tsToIsoOrNull(r.deletedAt ?? r.deleted_at),
  };
}

function coercePollOption(v: unknown): PollOption & { voteCount?: number; count?: number; voted?: boolean } {
  const r = isObject(v) ? v : {};
  return {
    id: typeof r.id === "string" ? r.id : "",
    text: typeof r.text === "string" ? r.text : "",
    voteCount:
      typeof r.voteCount === "number"
        ? r.voteCount
        : typeof r.count === "number"
          ? r.count
          : 0,
    ...(typeof r.voted === "boolean" ? { voted: r.voted } : {}),
    ...(typeof r.orderIdx === "number" ? { orderIdx: r.orderIdx } : {}),
  };
}

export function coercePoll(v: unknown): Poll {
  const r = isObject(v) ? v : {};
  const optionsRaw = Array.isArray(r.options) ? r.options : [];
  const userVotesRaw = Array.isArray(r.userVotes)
    ? r.userVotes
    : Array.isArray(r.user_votes)
      ? r.user_votes
      : [];
  // If the backend sent `options[].voted` and a flat `userVotes` is missing,
  // synthesize userVotes from the options the current user voted for.
  const synthesizedVotes = optionsRaw
    .filter((o) => isObject(o) && (o as Record<string, unknown>).voted === true)
    .map((o) => (o as Record<string, unknown>).id)
    .filter((id): id is string => typeof id === "string");
  return {
    id: typeof r.id === "string" ? r.id : "",
    messageId:
      typeof r.messageId === "string"
        ? r.messageId
        : typeof r.message_id === "string"
          ? r.message_id
          : "",
    question: typeof r.question === "string" ? r.question : "",
    allowMultiple:
      typeof r.allowMultiple === "boolean"
        ? r.allowMultiple
        : r.allow_multiple === 1 || r.allow_multiple === true,
    options: optionsRaw.map((o) => coercePollOption(o) as PollOption),
    userVotes: userVotesRaw.length > 0 ? userVotesRaw : synthesizedVotes,
  };
}

export function coerceMember(v: unknown): ConversationMember {
  const r = isObject(v) ? v : {};
  const user = "user" in r ? r.user : undefined;
  const role = r.role === "owner" || r.role === "manager" || r.role === "member" ? r.role : "member";
  return {
    userId:
      typeof r.userId === "string"
        ? r.userId
        : typeof r.user_id === "string"
          ? r.user_id
          : "",
    role,
    user: user ? coerceUser(user) : undefined,
  };
}

export function coerceConversation(v: unknown): Conversation {
  const r = isObject(v) ? v : {};
  const lastMessage = "lastMessage" in r ? r.lastMessage : undefined;
  const members = Array.isArray(r.members) ? r.members : undefined;
  return {
    id: typeof r.id === "string" ? r.id : "",
    type: r.type === "group" ? "group" : "direct",
    name: typeof r.name === "string" || r.name === null ? r.name : null,
    imageUrl:
      typeof r.imageUrl === "string"
        ? r.imageUrl
        : typeof r.image_url === "string"
          ? r.image_url
          : null,
    ownerId:
      typeof r.ownerId === "string"
        ? r.ownerId
        : typeof r.owner_id === "string"
          ? r.owner_id
          : null,
    lastMessage: lastMessage ? coerceMessage(lastMessage) : null,
    unreadCount: typeof r.unreadCount === "number" ? r.unreadCount : typeof r.unread_count === "number" ? r.unread_count : 0,
    members: members ? members.map((m) => coerceMember(m)) : undefined,
    createdAt: tsToIso(r.createdAt ?? r.created_at),
    updatedAt: tsToIso(r.updatedAt ?? r.updated_at),
  };
}

/* -------------------------------------------------------------------------- */
/*  Envelope normalizers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Backend `/api/conversations` returns `{conversations: [{
 *   conversation: {...snake},
 *   lastMessage: {...snake},
 *   unreadCount,
 *   role,
 *   members: [...]
 * }]}` OR (new) `{conversations: Conversation[]}` — accept both.
 */
export function normalizeConversationsList(data: unknown): Conversation[] {
  if (!isObject(data)) return [];
  const arr = Array.isArray(data.conversations) ? data.conversations : [];
  return arr.map((item) => {
    if (!isObject(item)) return coerceConversation(item);
    // Wrapped: {conversation, lastMessage, unreadCount, role, members}
    if ("conversation" in item && isObject(item.conversation)) {
      const merged = {
        ...(item.conversation as Record<string, unknown>),
        lastMessage: item.lastMessage,
        unreadCount: item.unreadCount ?? item.unread_count,
        members: item.members,
      };
      return coerceConversation(merged);
    }
    return coerceConversation(item);
  });
}

/** Backend `/api/conversations/:id` → `{conversation, members, myRole}`. */
export function normalizeConversationDetail(data: unknown): {
  conversation: Conversation;
  members: ConversationMember[];
  myRole: "owner" | "manager" | "member" | null;
} | null {
  if (!isObject(data)) return null;
  if (!data.conversation || !isObject(data.conversation)) return null;
  const conv = coerceConversation(data.conversation);
  const members = Array.isArray(data.members) ? data.members.map((m) => coerceMember(m)) : [];
  const myRole =
    data.myRole === "owner" || data.myRole === "manager" || data.myRole === "member"
      ? data.myRole
      : null;
  return { conversation: conv, members, myRole };
}

/** Backend `/api/conversations/:id/messages` → `{messages: Message[]}`. */
export function normalizeMessages(data: unknown): Message[] {
  if (!isObject(data)) return [];
  const arr = Array.isArray(data.messages) ? data.messages : [];
  return arr.map((m) => coerceMessage(m));
}

/** Auth responses: `{user, token}`. */
export function normalizeUser(data: unknown): User {
  if (!isObject(data) || !isObject(data.user)) {
    return coerceUser(data);
  }
  return coerceUser(data.user);
}

/** User search results: `{users: User[]}`. */
export function normalizeUsers(data: unknown): User[] {
  if (!isObject(data)) return [];
  const arr = Array.isArray(data.users) ? data.users : [];
  return arr.map((u) => coerceUser(u));
}

/** Reactions envelope: `{reactions: Reaction[]}`. */
export function normalizeReactions(data: unknown): Reaction[] {
  if (!isObject(data)) return [];
  return coerceReactions(data.reactions);
}

/** Message read-by envelope: `{readBy: MessageReadBy[]}`. */
export function normalizeMessageReadBy(data: unknown): MessageReadBy[] {
  if (!isObject(data)) return [];
  const arr = Array.isArray(data.readBy) ? data.readBy : [];
  return arr.map((v) => {
    const r = isObject(v) ? v : {};
    return {
      userId:
        typeof r.userId === "string"
          ? r.userId
          : typeof r.user_id === "string"
            ? r.user_id
            : "",
      username: typeof r.username === "string" ? r.username : "",
      displayName:
        typeof r.displayName === "string"
          ? r.displayName
          : typeof r.display_name === "string"
            ? r.display_name
            : "",
      lastReadMessageId:
        typeof r.lastReadMessageId === "string"
          ? r.lastReadMessageId
          : typeof r.last_read_message_id === "string"
            ? r.last_read_message_id
            : null,
    };
  });
}

/** User profile envelope: `{user: UserProfile}`. */
export function normalizeUserProfile(data: unknown): UserProfile {
  if (!isObject(data) || !isObject(data.user)) {
    return coerceUserProfile(data);
  }
  return coerceUserProfile(data.user);
}

/** Poll responses: `{poll, options, userVotes, totalVotes?}` or `{poll, message}`. */
export function normalizePollDetail(data: unknown): Poll | null {
  if (!isObject(data) || !isObject(data.poll)) return null;
  const poll = coercePoll(data.poll);
  const options = Array.isArray(data.options) ? data.options : [];
  if (options.length > 0) {
    poll.options = options.map((o) => coercePollOption(o) as PollOption);
  }
  // Backend puts `count` and `voted` on each option; synthesize userVotes.
  const synthesizedVotes = options
    .filter((o) => isObject(o) && (o as Record<string, unknown>).voted === true)
    .map((o) => (o as Record<string, unknown>).id)
    .filter((id): id is string => typeof id === "string");
  if (Array.isArray(data.userVotes) && data.userVotes.length > 0) {
    poll.userVotes = data.userVotes as string[];
  } else if (synthesizedVotes.length > 0) {
    poll.userVotes = synthesizedVotes;
  }
  return poll;
}

/** Poll create: `{poll, message, messageId}`. */
export function normalizePollCreate(data: unknown): { poll: Poll; message?: Message | null; messageId?: string | null } | null {
  if (!isObject(data)) return null;
  if (!isObject(data.poll)) return null;
  const poll = coercePoll(data.poll);
  const message = "message" in data && isObject(data.message) ? coerceMessage(data.message) : null;
  const messageId = typeof data.messageId === "string" ? data.messageId : poll.messageId;
  return { poll, message, messageId };
}

/**
 * Normalize a server WS event into the shape declared by `ServerEvent` in
 * `apps/web/src/lib/ws.ts`. The Durable Object broadcasts each event with
 * its payload in a type-specific field at the top level (e.g. `{type,
 * message}` for messages, `{type, userId, online, lastSeen}` for presence,
 * `{type, messageId, conversationId}` for message deletes). The frontend
 * code, however, uniformly reads `event.data` — so the adapter is
 * responsible for *reshaping the envelope*, not just coercing the inner
 * fields.
 *
 * Without this reshape, every WS event arrives missing `data` and the
 * recipient's UI silently drops the message until the next REST refresh.
 */
export function normalizeWsMessage(raw: unknown): unknown {
  if (!isObject(raw)) return raw;
  const t = raw.type;
  if (typeof t !== "string") return raw;

  // `data` already present (typed-shape payloads from a future server, or
  // round-trips) — coerce its contents but don't drop the envelope.
  if ("data" in raw && isObject(raw.data)) {
    if (t === "message") {
      return { type: "message", data: coerceMessage(raw.data) };
    }
    if (t === "message_edit") {
      // raw.data is already an object here (guarded by `isObject` above).
      const inner = raw.data;
      const dataObj: Record<string, unknown> = { ...inner };
      if ("message" in inner && isObject(inner.message)) {
        dataObj.message = coerceMessage(inner.message);
      }
      return { type: "message_edit", data: dataObj };
    }
    if (t === "reaction_update") {
      // Wire: {type, data: {messageId, conversationId, reactions: [...]}}
      const inner = raw.data;
      const dataObj: Record<string, unknown> = { ...inner };
      if ("reactions" in inner && Array.isArray(inner.reactions)) {
        dataObj.reactions = coerceReactions(inner.reactions);
      }
      return { type: "reaction_update", data: dataObj };
    }
    return raw;
  }

  switch (t) {
    case "message": {
      if ("message" in raw && isObject(raw.message)) {
        return { type: "message", data: coerceMessage(raw.message) };
      }
      return raw;
    }
    case "message_edit": {
      // Wire: {type: "message_edit", message: ApiMessage}
      const data: Record<string, unknown> = {};
      if ("message" in raw && isObject(raw.message)) data.message = coerceMessage(raw.message);
      if (typeof raw.messageId === "string") data.messageId = raw.messageId;
      if (typeof raw.conversationId === "string") data.conversationId = raw.conversationId;
      if ("editedAt" in raw) data.editedAt = raw.editedAt;
      return { type: "message_edit", data };
    }
    case "message_delete": {
      // Wire: {type: "message_delete", messageId, conversationId}
      const data: Record<string, unknown> = {};
      if (typeof raw.messageId === "string") data.messageId = raw.messageId;
      if (typeof raw.conversationId === "string") data.conversationId = raw.conversationId;
      if ("deletedAt" in raw) data.deletedAt = raw.deletedAt;
      return { type: "message_delete", data };
    }
    case "presence": {
      // Wire: {type: "presence", userId, online, lastSeen}
      const data: Record<string, unknown> = {};
      if ("userId" in raw) data.userId = raw.userId;
      if ("online" in raw) data.online = raw.online;
      if ("lastSeen" in raw) data.lastSeen = raw.lastSeen;
      return { type: "presence", data };
    }
    case "typing": {
      // Wire: {type: "typing", userId, isTyping, lastSeen?}
      const data: Record<string, unknown> = {};
      if ("userId" in raw) data.userId = raw.userId;
      if ("isTyping" in raw) data.isTyping = raw.isTyping;
      return { type: "typing", data };
    }
    case "poll_update": {
      // Wire: {type: "poll_update", poll: ApiPoll} — the poll shape itself
      // (with options + userVotes), not the {poll, options, userVotes}
      // wrapper that `normalizePollDetail` expects.
      if ("poll" in raw && isObject(raw.poll)) {
        return { type: "poll_update", data: coercePoll(raw.poll) };
      }
      return raw;
    }
    case "reaction_update": {
      // Wire: {type: "reaction_update", messageId, conversationId,
      //        reactions: [{emoji, count, myReacted}]}
      // Frontend: {type: "reaction_update", data: {messageId, conversationId,
      //        reactions: Reaction[]}}
      const data: Record<string, unknown> = {};
      if (typeof raw.messageId === "string") data.messageId = raw.messageId;
      if (typeof raw.conversationId === "string") data.conversationId = raw.conversationId;
      if ("reactions" in raw && Array.isArray(raw.reactions)) {
        data.reactions = coerceReactions(raw.reactions);
      }
      return { type: "reaction_update", data };
    }
    default:
      // pong, ping, hello, ready, and any future event types pass through
      // unchanged — they don't carry a payload that needs reshaping.
      return raw;
  }
}
